import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getAmplitudeClient } from '@/features/amplitude';
import { useWorkspace } from '@/features/workspace';
import { useWorkspaceId } from '@/hooks/router';
import { getCareersPageQueryKey } from '@/queries/careers/keys';
import {
  usePatchCareersPageMutation,
  usePostCareersPageSubdomainAvailableMutation,
} from '@/queries/careers/mutations';
import { useGetCareersPage } from '@/queries/careers/queries';
import { HandlersWorkspaceWithTrials } from '@/queries/types';
import { queryClient } from '@/utils/reactQuery';
import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DSButton, DSContextMenu } from '@hundred5/design-system';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { formatDistanceStrict } from 'date-fns/formatDistanceStrict';
import { Form, FormikProvider, useFormik } from 'formik';
import { debounce } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { object, string } from 'yup';
import { copyText, FormikInputField, Icon, Switch } from '../../common';
import { useNotifications } from '../../notifications';
import { getHostnameFromApiUrl } from './careers-job-opening-list/careers-job-opening-list.utils';

interface ShareCareersModalProps {
  open: boolean;
  onClose?: () => void;
}

export function ShareCareersModal({ open, onClose }: ShareCareersModalProps) {
  const { addNotification } = useNotifications();
  const workspaceId = useWorkspaceId();
  const query = useGetCareersPage(Number(workspaceId));
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const mutation = usePatchCareersPageMutation();
  const subdomainAvailableMutation =
    usePostCareersPageSubdomainAvailableMutation();
  const apiUrl = import.meta.env.VITE_API_URL;
  const url = query.data?.subdomain
    ? `${query.data?.subdomain}.${getHostnameFromApiUrl(apiUrl)}`
    : '';
  const lastSavedTimeDistance = query.data?.updatedAt
    ? differenceInMinutes(Date.now(), query.data?.updatedAt) < 1
      ? 'less than a minute'
      : formatDistanceStrict(query.data?.updatedAt, Date.now())
    : undefined;

  const copyLink = () => {
    copyText(url);
    addNotification({ type: 'copied_to_clipboard' });
  };
  const initialValues = { subdomain: query.data?.subdomain ?? '' };
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const workspace = useWorkspace();
  const hasPatchedRef = useRef(false);

  const validationSchema = object().shape({
    subdomain: string()
      .strict()
      .trim()
      .required('You must provide a company name')
      .test(
        'starts-with-letter',
        'Subdomain must start with a letter. Try adding letters before the numbers.',
        (value) => !value || /^[a-zA-Z]/.test(value)
      )
      .test(
        'valid-characters',
        'This name uses characters that are not allowed.',
        (value) => !value || /^[a-z]([-a-z0-9]*[a-z0-9])?$/.test(value)
      ),
  });

  const verifySubdomain = async (value: string) => {
    if (value) {
      const available = await subdomainAvailableMutation.mutateAsync({
        workspaceId: Number(workspaceId),
        data: value,
      });
      if (!available) {
        formik.setFieldError(
          'subdomain',
          'This name is already taken, please choose another'
        );
      }
      return available;
    }
  };

  const debounceVerifySubdomain = useRef(
    debounce((value: string) => verifySubdomain(value), 500)
  );

  const generateUniqueSlug = useCallback(
    async (
      workspace: HandlersWorkspaceWithTrials
    ): Promise<{ attempt: string; error: boolean }> => {
      const base =
        workspace.company?.toLowerCase() ||
        workspace.name.split(/[^a-zA-Z0-9]/)[0].toLowerCase();

      for (let attemptCount = 0; attemptCount <= 3; attemptCount++) {
        const suffix =
          attemptCount === 0 ? '' : Math.floor(1000 + Math.random() * 9000);
        const attempt = base + suffix;

        if (attempt.length && /^[a-z]([-a-z0-9]*[a-z0-9])?$/.test(attempt)) {
          const isAvailable = await subdomainAvailableMutation.mutateAsync({
            workspaceId: Number(workspaceId),
            data: attempt,
          });

          if (isAvailable) {
            return { attempt, error: false };
          }
        }
      }

      return { attempt: base, error: true };
    },
    [subdomainAvailableMutation, workspaceId]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema,
    onSubmit: () => {
      mutation.mutate(
        {
          workspaceId: Number(workspaceId),
          data: {
            subdomain: formik.values.subdomain,
          },
        },
        {
          onSuccess: async () => {
            addNotification({ type: 'saved' });
            await queryClient.invalidateQueries({
              queryKey: getCareersPageQueryKey(Number(workspaceId)),
            });
            setIsEditing(false);
          },
          onError: () => {
            addNotification({
              type: 'invalid_subdomain',
            });
          },
        }
      );

      amplitude?.logEvent('careers/editing URL', {
        'workspace id': workspaceId,
      });
      posthog?.capture('hire_admin_app:careers/editing URL', {
        'workspace id': workspaceId,
      });
    },
    initialTouched: {
      subdomain: true,
    },
  });

  useEffect(() => {
    if (
      workspace &&
      query.data &&
      !query.data.subdomain &&
      !hasPatchedRef.current
    ) {
      hasPatchedRef.current = true;
      generateUniqueSlug(workspace).then((result) => {
        if (!result.error) {
          mutation.mutate(
            {
              workspaceId: workspace.id,
              data: {
                subdomain: result.attempt,
              },
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: getCareersPageQueryKey(Number(workspace.id)),
                });
              },
            }
          );
        } else {
          formik.setFieldValue('subdomain', result.attempt);
          formik.validateField('subdomain');
        }
      });
    }
  }, [query.data, mutation, workspace, generateUniqueSlug, formik]);

  useEffect(() => {
    if (
      query.data &&
      formik.values.subdomain !== query.data.subdomain &&
      formik.values.subdomain &&
      formik.values.subdomain.match(/^[a-z]([-a-z0-9]*[a-z0-9])?$/)
    ) {
      debounceVerifySubdomain.current(formik.values.subdomain);
    }
  }, [formik.values.subdomain, query.data]);

  return (
    <StyledContextMenu open={open} position={{ right: '24px', top: '52px' }}>
      <Header>
        <h3>Your careers page</h3>
      </Header>
      {isEditing || (query.data && !query.data?.subdomain) ? (
        <Section>
          <FormikProvider value={formik}>
            <Form>
              <EditSubdomainWrapper>
                <SubdomainInput
                  name="subdomain"
                  required
                  value={formik.values.subdomain}
                />
                .{getHostnameFromApiUrl(apiUrl)}
              </EditSubdomainWrapper>
              <ButtonContainer>
                <DSButton
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    if (!query.data?.subdomain) {
                      onClose?.();
                    }
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </DSButton>
                <DSButton
                  size="small"
                  type="submit"
                  disabled={
                    (!formik.isValid && formik.dirty) ||
                    formik.initialValues.subdomain === formik.values.subdomain
                  }
                >
                  Save
                </DSButton>
              </ButtonContainer>
            </Form>
          </FormikProvider>
        </Section>
      ) : (
        <Section>
          <PublishedContainer>
            <IconAndTextContainer>
              <IconContainer>
                <Icon
                  icon={regular('globe')}
                  color="purple-60"
                  fontSize="36px"
                />
              </IconContainer>
              <TextContainer>
                <Wrapper>
                  <Bold>
                    {query.data?.published ? 'Published' : 'Not published'}
                  </Bold>
                  <Icon
                    icon={regular('circle-info')}
                    color="purple-60"
                    fontSize="12px"
                    data-rh="Published pages can be indexed by search engines and may appear in search results."
                  />
                </Wrapper>
                <LastUpdatedText>
                  Last updated {lastSavedTimeDistance} ago
                </LastUpdatedText>
              </TextContainer>
            </IconAndTextContainer>
            <Switch
              value={query.data?.published ?? false}
              onChange={() => {
                mutation.mutate(
                  {
                    workspaceId: Number(workspaceId),
                    data: {
                      published: !query.data?.published,
                    },
                  },
                  {
                    onSuccess: async () => {
                      addNotification({
                        type: query.data?.published
                          ? 'careers_page_unpublished'
                          : 'careers_page_published',
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getCareersPageQueryKey(Number(workspaceId)),
                      });
                    },
                  }
                );
                amplitude?.logEvent('careers/published on/off', {
                  'workspace id': workspaceId,
                });
                posthog?.capture('hire_admin_app:careers/published on/off', {
                  'workspace id': workspaceId,
                });
              }}
            />
          </PublishedContainer>
          <CopyLinkContainer
            onClick={copyLink}
            aria-label="Copy link to clipboard"
          >
            {url} <Icon icon={regular('copy')} />
          </CopyLinkContainer>
          <DSButton
            size="small"
            variant="secondary"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            Edit URL
          </DSButton>
        </Section>
      )}
    </StyledContextMenu>
  );
}

const StyledContextMenu = styled(DSContextMenu)`
  padding: 0;
  width: 400px;
`;

const Header = styled.div`
  padding: 36px 36px 16px 36px;

  border-bottom: 1px solid ${(props) => props.theme.colors.purple[10]};

  h3 {
    margin: 0;
  }
`;

const Section = styled.div`
  padding: 16px 36px 24px 36px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 54px;
`;

const EditSubdomainWrapper = styled.div`
  padding: 24px 12px;
  border-radius: 8px;
  background: ${(props) => props.theme.colors.purple[5]};
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  justify-content: flex-start;
  width: 100%;
`;

const SubdomainInput = styled(FormikInputField)`
  width: 160px;
  margin-right: 4px;

  span {
    position: absolute;
    top: 64px;
    left: -12px;
    width: 328px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
`;

const IconAndTextContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const PublishedContainer = styled.div`
  display: flex;
  height: 80px;
  padding: 8px 22px 8px 8px;
  flex-direction: row;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.purple[10]};
  margin-bottom: 10px;
  justify-content: space-between;
`;

const IconContainer = styled.div`
  background-color: ${(props) => props.theme.colors.purple['2.5']};
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  width: 64px;
  height: 64px;
  margin-right: 12px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 176px;
`;

const Bold = styled.div`
  color: ${(props) => props.theme.colors.purple[100]};
  font-weight: 600;
  margin-right: 4px;
`;

const LastUpdatedText = styled.div`
  color: ${(props) => props.theme.colors.purple[60]};
  font-size: 12px;
  font-weight: 500;
`;

const CopyLinkContainer = styled.div`
  display: flex;
  padding: 12px;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.purple[5]};
  margin-bottom: 32px;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background-color: ${(props) => props.theme.colors.purple[10]};
  }
`;
